import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { createOrganization, fetchSubscriptionPlans } from '../../../actioncreators/actioncreators';
import { setSuperAdminOrgFilter } from '../../../actions/loanAction';
import CountryDropdown from '../../../fields/CountryDropdown';
import StateDropdown from '../../../fields/StateDropdown';
import CityDropdown from '../../../fields/CityDropdown';
import PincodeDropdown from '../../../fields/PincodeDropdown';

const organizationSizes = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const organizationTypes = ["Govt. Office", "IT", "Finance", "Healthcare", "Education", "Manufacturing", "Retail", "Other"];

/* ── Inline styles ── */
const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f1f5f9',
    padding: '24px',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    border: 'none',
    background: 'none',
    color: '#64748b',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '4px 0',
    marginBottom: '16px',
    fontWeight: 500,
  },
  pageTitle: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#0f172a',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '13px',
    color: '#64748b',
    margin: '0 0 24px 0',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '18px 24px 14px',
    borderBottom: '1px solid #f1f5f9',
  },
  sectionIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
    letterSpacing: '0.1px',
  },
  sectionSubLabel: {
    fontSize: '11px',
    color: '#94a3b8',
    margin: 0,
  },
  sectionBody: {
    padding: '20px 24px',
  },
  grid3: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '18px',
  },
  fieldLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: '#475569',
    marginBottom: '5px',
    letterSpacing: '0.1px',
  },
  input: {
    width: '100%',
    height: '38px',
    padding: '0 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#0f172a',
    backgroundColor: '#f8fafc',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  select: {
    width: '100%',
    height: '38px',
    padding: '0 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#0f172a',
    backgroundColor: '#f8fafc',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  phoneWrap: {
    display: 'flex',
    height: '38px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#f8fafc',
  },
  phonePrefix: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 10px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#475569',
    backgroundColor: '#f1f5f9',
    borderRight: '1px solid #e2e8f0',
    whiteSpace: 'nowrap',
  },
  phoneInput: {
    flex: 1,
    border: 'none',
    padding: '0 12px',
    fontSize: '13px',
    color: '#0f172a',
    backgroundColor: '#f8fafc',
    outline: 'none',
    width: '100%',
  },
  divider: {
    height: '1px',
    backgroundColor: '#f1f5f9',
    border: 'none',
    margin: 0,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '20px 24px',
    borderTop: '1px solid #f1f5f9',
    backgroundColor: '#fafbfc',
  },
  submitBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 28px',
    backgroundColor: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  cancelBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '10px 20px',
    backgroundColor: '#fff',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  errorText: {
    fontSize: '11px',
    color: '#ef4444',
    marginTop: '4px',
  },
};

const Field = ({ label, error, children }) => (
  <div>
    <label style={styles.fieldLabel}>{label}</label>
    {children}
    {error && <p style={styles.errorText}>{error}</p>}
  </div>
);

const SectionHeader = ({ icon, bg, title, subtitle }) => (
  <div style={styles.sectionHeader}>
    <div style={{ ...styles.sectionIcon, backgroundColor: bg }}>{icon}</div>
    <div>
      <p style={styles.sectionLabel}>{title}</p>
      {subtitle && <p style={styles.sectionSubLabel}>{subtitle}</p>}
    </div>
  </div>
);

// RFC 5322-aligned email regex. Covers the practical set of valid addresses
// without the over-permissiveness of naive checks like "contains @".
//   - Local part: printable chars, dots, plus, hyphen (no leading/trailing dot)
//   - Domain: one or more labels separated by dots, each label alphanumeric + hyphen
//   - TLD: at least 2 alpha chars
const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

const validateEmail = (raw) => {
  const trimmed = raw.trim();
  if (!trimmed) return '';                          // empty is allowed (field not required)
  if (!EMAIL_RE.test(trimmed)) {
    // Give a specific hint for the most common mistakes
    if (!trimmed.includes('@'))  return 'Email must contain @  (e.g. user@example.com)';
    if (trimmed.startsWith('@')) return 'Local part is missing before @  (e.g. user@example.com)';
    if (trimmed.endsWith('@'))   return 'Domain is missing after @  (e.g. user@example.com)';
    if ((trimmed.match(/@/g) || []).length > 1) return 'Email must contain exactly one @';
    return 'Invalid email format (e.g. user@example.com)';
  }
  return '';
};

const CreateOrganization = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [gstError, setGstError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [fullNameError, setFullNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    orgName: '',
    orgAddress: '',
    country: 'India',
    countryCode: 'IN',
    state: '',
    city: '',
    pincode: '',
    phoneCode: '+91',
    phoneNumber: '',
    orgSize: '',
    orgType: '',
    GSTNumber: '',
    package_id: '',
    max_users: '',
    subscription_start_date: '',
    subscription_end_date: '',
    daily_charge_per_user: '',
    beacon_tracking_enabled: true,
  });

  useEffect(() => {
    fetchSubscriptionPlans()
      .then(res => setPlans(res?.data?.data || []))
      .catch(() => {});
  }, []);

  const validateGSTNumber = (gst) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    if (!gst) return { isValid: true, message: '' };
    if (!gstRegex.test(gst)) return { isValid: false, message: 'Invalid GST Number format' };
    return { isValid: true, message: '' };
  };

  const validatePhone = (digits) => {
    if (!digits) return 'Phone number is required.';
    if (digits.length !== 10) return 'Phone number must be exactly 10 digits.';
    return '';
  };

  // date picker state: { day, month, year } strings
  const [startDate, setStartDate] = useState({ day: '', month: '', year: '' });
  const [endDate, setEndDate] = useState({ day: '', month: '', year: '' });

  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  const days = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 201 }, (_, i) => String(1900 + i));

  const buildDateValue = ({ day, month, year }) => {
    if (!day || !month || !year) return '';
    return `${year}-${month}-${day}`;
  };

  const handleDatePartChange = (which, part, value) => {
    const setter = which === 'start' ? setStartDate : setEndDate;
    const errSetter = which === 'start' ? setStartDateError : setEndDateError;
    const label = which === 'start' ? 'Start date' : 'End date';
    setter(prev => {
      const next = { ...prev, [part]: value };
      const full = buildDateValue(next);
      const y = parseInt(next.year, 10);
      if (full && (isNaN(y) || y < 1900 || y > 2100)) {
        errSetter(`${label} year must be between 1900 and 2100.`);
      } else {
        errSetter('');
      }
      setFormData(fd => ({ ...fd, [which === 'start' ? 'subscription_start_date' : 'subscription_end_date']: full }));
      return next;
    });
  };

  const validateDate = (value, label) => {
    if (!value) return '';
    const parts = value.split('-');
    if (parts.length !== 3) return `${label} is not a valid date.`;
    const year = parseInt(parts[0], 10);
    if (isNaN(year) || year < 1900 || year > 2100) return `${label} year must be between 1900 and 2100.`;
    const d = new Date(value);
    if (isNaN(d.getTime())) return `${label} is not a valid date.`;
    return '';
  };

  const validateFullName = (name) => {
    const trimmed = name.trim().replace(/\s+/g, ' ');
    if (!trimmed) return 'Full name is required.';
    if (trimmed.length < 2) return 'Full name must be at least 2 characters.';
    if (trimmed.length > 100) return 'Full name must not exceed 100 characters.';
    if (!/[a-zA-Z]/.test(trimmed)) return 'Full name must contain at least one letter.';
    if (/[^a-zA-ZÀ-ɏऀ-ॿ\s'\-.]/.test(trimmed))
      return "Full name may only contain letters, spaces, hyphens, apostrophes, and dots.";
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      // Strip anything that isn't a digit — handles typing and paste
      const digits = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, phoneNumber: digits }));
      setPhoneError(validatePhone(digits));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'GSTNumber') {
      const v = validateGSTNumber(value);
      setGstError(v.isValid ? '' : v.message);
    }
    if (name === 'email') {
      setEmailError(validateEmail(value));
    }
    if (name === 'fullName') {
      setFullNameError(validateFullName(value));
    }
    if (name === 'max_users') {
      setFormData(prev => ({ ...prev, max_users: value.replace(/^0+(?=\d)/, '') }));
      return;
    }
  };

  // Trim email on blur so "  user@example.com  " is silently cleaned up
  const handleEmailBlur = (e) => {
    const trimmed = e.target.value.trim();
    setFormData(prev => ({ ...prev, email: trimmed }));
    setEmailError(validateEmail(trimmed));
  };

  const handleCountryChange = (selectedCountry) => {
    setFormData(prev => ({
      ...prev,
      country: selectedCountry.name,
      countryCode: selectedCountry.code,
      state: '',
      city: '',
      phoneCode: selectedCountry.phoneCode,
    }));
  };

  const handleStateChange = (e) => {
    setFormData(prev => ({ ...prev, state: e.target.value, city: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trim and re-validate email at submit time in case the user never blurred
    const trimmedEmail = formData.email.trim();
    setFormData(prev => ({ ...prev, email: trimmedEmail }));
    const emailErr = validateEmail(trimmedEmail);
    if (emailErr) {
      setEmailError(emailErr);
      return;
    }

    // Trim and validate full name
    const trimmedFullName = formData.fullName.trim().replace(/\s+/g, ' ');
    setFormData(prev => ({ ...prev, fullName: trimmedFullName }));
    const fnErr = validateFullName(trimmedFullName);
    if (fnErr) {
      setFullNameError(fnErr);
      return;
    }

    // Validate phone number
    const trimmedPhone = formData.phoneNumber.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, phoneNumber: trimmedPhone }));
    const phoneErr = validatePhone(trimmedPhone);
    if (phoneErr) {
      setPhoneError(phoneErr);
      return;
    }

    const sdErr = validateDate(formData.subscription_start_date, 'Start date');
    if (sdErr) { setStartDateError(sdErr); return; }
    const edErr = validateDate(formData.subscription_end_date, 'End date');
    if (edErr) { setEndDateError(edErr); return; }

    const gstValidation = validateGSTNumber(formData.GSTNumber);
    if (!gstValidation.isValid) {
      setGstError(gstValidation.message);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        org_name: formData.orgName,
        org_type: formData.orgType,
        org_size: formData.orgSize,
        full_name: trimmedFullName,
        email: trimmedEmail,
        mobile: `${formData.phoneCode}${formData.phoneNumber}`,
        address_line_1: formData.orgAddress,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        pincode: formData.pincode,
        gst_number: formData.GSTNumber,
        package_id: formData.package_id || null,
        max_users: formData.max_users ? parseInt(formData.max_users) : null,
        subscription_start_date: formData.subscription_start_date || null,
        subscription_end_date: formData.subscription_end_date || null,
        daily_charge_per_user: formData.daily_charge_per_user ? parseFloat(formData.daily_charge_per_user) : null,
        beacon_tracking_enabled: Boolean(formData.beacon_tracking_enabled),
      };
      const response = await createOrganization(payload);
      // Select the newly created org as the Super Admin's active org context.
      // Without this, superAdminSelectedOrgId in Redux stays whatever it was
      // before (often null or a previously selected org), so the very next
      // action a Super Admin takes here — e.g. creating a Manager, then a
      // Sub-Organization for that Manager — silently gets scoped to the OLD
      // org instead of the one just created. The new sub-org would then be
      // invisible in the header dropdown / dashboard (both scoped by actual
      // org_id) even though it looks fine on the Sub-Organizations list page
      // (which reads the same stale org id, so it's "consistent" but wrong).
      const newOrg = response?.data?.data;
      if (newOrg?.org_id) {
        dispatch(setSuperAdminOrgFilter(newOrg.org_id, {
          org_id: newOrg.org_id,
          org_name: newOrg.org_name,
          status: 'ACTIVE',
        }));
      }
      toast.success('Organization created successfully');
      navigate('/home/super-admin/organizations');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const dropdownClass = "mt-0 block w-full rounded-md border border-gray-300 shadow-sm text-xs";

  return (
    <div style={styles.page}>

      {/* Back */}
      <button type="button" style={styles.backBtn} onClick={() => navigate('/home/super-admin/organizations')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M19 12H5M12 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        Back to Organizations
      </button>

      {/* Page heading */}
      <h1 style={styles.pageTitle}>Create Organization</h1>
      <p style={styles.pageSubtitle}>Register a new organization on the platform</p>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* ── Account Information ── */}
          <div style={styles.card}>
            <SectionHeader
              icon="👤"
              bg="#ede9fe"
              title="Account Information"
              subtitle="Admin contact details for this organization"
            />
            <div style={styles.sectionBody}>
              <div style={styles.grid3}>
                <Field label="Email *" error={emailError}>
                  <input
                    style={{ ...styles.input, ...(emailError ? styles.inputError : {}) }}
                    type="text"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleEmailBlur}
                    placeholder="admin@company.com"
                    required
                    autoComplete="email"
                  />
                </Field>
                <Field label="Full Name *" error={fullNameError}>
                  <input
                    style={{ ...styles.input, ...(fullNameError ? styles.inputError : {}) }}
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      const trimmed = e.target.value.trim().replace(/\s+/g, ' ');
                      setFormData(prev => ({ ...prev, fullName: trimmed }));
                      setFullNameError(validateFullName(trimmed));
                    }}
                    placeholder="John Doe"
                    required
                  />
                </Field>
                <Field label="Phone Number *" error={phoneError}>
                  <div style={{ ...styles.phoneWrap, ...(phoneError ? { borderColor: '#ef4444' } : {}) }}>
                    <span style={styles.phonePrefix}>{formData.phoneCode}</span>
                    <input
                      style={styles.phoneInput}
                      type="tel"
                      inputMode="numeric"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      onBlur={() => setPhoneError(validatePhone(formData.phoneNumber))}
                      placeholder="9999999999"
                      maxLength={10}
                      required
                    />
                  </div>
                </Field>
              </div>
            </div>
          </div>

          {/* ── Organization Details ── */}
          <div style={styles.card}>
            <SectionHeader
              icon="🏢"
              bg="#dbeafe"
              title="Organization Details"
              subtitle="Basic information about the organization"
            />
            <div style={styles.sectionBody}>
              <div style={styles.grid3}>
                <Field label="Organization Name *">
                  <input style={styles.input} type="text" name="orgName" value={formData.orgName} onChange={handleInputChange} placeholder="Acme Corporation" required />
                </Field>
                <Field label="Organization Type *">
                  <select style={styles.select} name="orgType" value={formData.orgType} onChange={handleInputChange} required>
                    <option value="">Select type</option>
                    {organizationTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Organization Size *">
                  <select style={styles.select} name="orgSize" value={formData.orgSize} onChange={handleInputChange} required>
                    <option value="">Select size</option>
                    {organizationSizes.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="GST Number (optional)" error={gstError}>
                  <input
                    style={{ ...styles.input, ...(gstError ? styles.inputError : {}) }}
                    type="text"
                    name="GSTNumber"
                    value={formData.GSTNumber}
                    onChange={handleInputChange}
                    placeholder="22AAAAA0000A1Z5"
                  />
                </Field>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
                <input
                  type="checkbox"
                  id="beacon_tracking_enabled"
                  checked={formData.beacon_tracking_enabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, beacon_tracking_enabled: e.target.checked }))}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="beacon_tracking_enabled" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                  Enable BLE (Beacon) Attendance
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                    When on, this organization gets the Beacons menu, the User Presence tab, student alerts and all BLE permissions automatically. When off, all of these are hidden and BLE access is blocked.
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* ── Address ── */}
          <div style={styles.card}>
            <SectionHeader
              icon="📍"
              bg="#dcfce7"
              title="Address"
              subtitle="Registered office location"
            />
            <div style={styles.sectionBody}>
              <div style={styles.grid3}>
                <Field label="Address Line 1 *">
                  <input style={styles.input} type="text" name="orgAddress" value={formData.orgAddress} onChange={handleInputChange} placeholder="Enter street address" required />
                </Field>
                <Field label="Country *">
                  <CountryDropdown
                    name="country"
                    value={formData.country}
                    onChange={handleCountryChange}
                    className={dropdownClass}
                    required
                  />
                </Field>
                <Field label="State / Province *">
                  <StateDropdown
                    name="state"
                    countryCode={formData.countryCode}
                    value={formData.state}
                    onChange={handleStateChange}
                    className={dropdownClass}
                    required
                  />
                </Field>
                <Field label="City *">
                  <CityDropdown
                    name="city"
                    countryName={formData.country}
                    stateName={formData.state}
                    value={formData.city}
                    onChange={handleInputChange}
                    className={dropdownClass}
                    required
                  />
                </Field>
                <Field label="Pincode / Zipcode *">
                  <PincodeDropdown
                    name="pincode"
                    countryName={formData.country}
                    stateName={formData.state}
                    cityName={formData.city}
                    value={formData.pincode}
                    onChange={handleInputChange}
                    placeholder="Enter pincode"
                    className="mt-0 block w-full rounded-md border border-gray-300 h-9 text-xs px-3"
                    required
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* ── Subscription Plan ── */}
          <div style={styles.card}>
            <SectionHeader
              icon="💳"
              bg="#fef9c3"
              title="Subscription Plan"
              subtitle="Optional — can be assigned later"
            />
            <div style={styles.sectionBody}>
              <div style={styles.grid3}>
                <Field label="Plan">
                  <select style={styles.select} name="package_id" value={formData.package_id} onChange={handleInputChange}>
                    <option value="">No plan (assign later)</option>
                    {plans.filter(p => p.status === 'ACTIVE').map(p => <option key={p.package_id} value={p.package_id}>{p.package_name}</option>)}
                  </select>
                </Field>
                <Field label="Max Users">
                  <input
                    style={styles.input}
                    type="number"
                    name="max_users"
                    value={formData.max_users}
                    onChange={handleInputChange}
                    onFocus={(e) => e.target.select()}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v) && v >= 1) setFormData(prev => ({ ...prev, max_users: String(v) }));
                    }}
                    placeholder="10"
                    min="1"
                  />
                </Field>
                <Field label="Daily Charge / User (₹)">
                  <input style={styles.input} type="number" step="0.01" name="daily_charge_per_user" value={formData.daily_charge_per_user} onChange={handleInputChange} placeholder="0.00" min="0" />
                </Field>
                <Field label="Start Date" error={startDateError}>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <select
                      style={{ ...styles.input, flex: 1 }}
                      value={startDate.day}
                      onChange={e => handleDatePartChange('start', 'day', e.target.value)}
                    >
                      <option value="">Day</option>
                      {days.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select
                      style={{ ...styles.input, flex: 2 }}
                      value={startDate.month}
                      onChange={e => handleDatePartChange('start', 'month', e.target.value)}
                    >
                      <option value="">Month</option>
                      {months.map((m, i) => <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>)}
                    </select>
                    <select
                      style={{ ...styles.input, flex: 2 }}
                      value={startDate.year}
                      onChange={e => handleDatePartChange('start', 'year', e.target.value)}
                    >
                      <option value="">Year</option>
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </Field>
                <Field label="End Date" error={endDateError}>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <select
                      style={{ ...styles.input, flex: 1 }}
                      value={endDate.day}
                      onChange={e => handleDatePartChange('end', 'day', e.target.value)}
                    >
                      <option value="">Day</option>
                      {days.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select
                      style={{ ...styles.input, flex: 2 }}
                      value={endDate.month}
                      onChange={e => handleDatePartChange('end', 'month', e.target.value)}
                    >
                      <option value="">Month</option>
                      {months.map((m, i) => <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>)}
                    </select>
                    <select
                      style={{ ...styles.input, flex: 2 }}
                      value={endDate.year}
                      onChange={e => handleDatePartChange('end', 'year', e.target.value)}
                    >
                      <option value="">Year</option>
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </Field>
              </div>
            </div>
          </div>

        </div>

        {/* ── Footer actions ── */}
        <div style={styles.footer}>
          <button type="submit" disabled={loading} style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}>
            {loading
              ? <><Spinner size="sm" />Creating...</>
              : <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  Create Organization
                </>
            }
          </button>
          <button type="button" disabled={loading} style={styles.cancelBtn} onClick={() => navigate('/home/super-admin/organizations')}>
            Cancel
          </button>
        </div>

      </form>
    </div>
  );
};

export default CreateOrganization;
